import { getRelativeLocation, getGridLocation, orthogonal, } from '../gridview/gridview';
import { directionToPosition, } from '../dnd/droptarget';
import { html5Backend, pointerBackend } from '../dnd/backend';
import { tail, sequenceEquals, remove } from '../array';
import { DockviewPanel } from './dockviewPanel';
import { CompositeDisposable, Disposable } from '../lifecycle';
import { Event, Emitter, addDisposableListener } from '../events';
import { Watermark } from './components/watermark/watermark';
import { sequentialNumberGenerator } from '../math';
import { DefaultDockviewDeserialzier } from './deserializer';
import { DockviewUnhandledDragOverEvent, isGroupOptionsWithGroup, isGroupOptionsWithPanel, isPanelOptionsWithGroup, isPanelOptionsWithPanel, } from './options';
import { BaseGrid, toTarget, } from '../gridview/baseComponentGridview';
import { DockviewApi } from '../api/component.api';
import { Orientation } from '../splitview/splitview';
import { DockviewDidDropEvent, DockviewWillDropEvent, } from './dockviewGroupPanelModel';
import { DockviewWillShowOverlayLocationEvent, } from './events';
import { DockviewGroupPanel } from './dockviewGroupPanel';
import { DockviewPanelModel } from './dockviewPanelModel';
import { getPanelData } from '../dnd/dataTransfer';
import { Overlay } from '../overlay/overlay';
import { addTestId, Classnames, getDockviewTheme, onDidWindowResizeEnd, onDidWindowMoveEnd, toggleClass, watchElementResize, } from '../dom';
import { DockviewFloatingGroupPanel } from './dockviewFloatingGroupPanel';
import { DEFAULT_FLOATING_GROUP_OVERFLOW_SIZE, DEFAULT_FLOATING_GROUP_POSITION, DESERIALIZATION_POPOUT_DELAY_MS, } from '../constants';
import { OverlayRenderContainer, } from '../overlay/overlayRenderContainer';
import { PopoutWindow } from '../popoutWindow';
import { StrictEventsSequencing } from './strictEventsSequencing';
import { PopupService } from './components/popupService';
import { ContextMenuController } from './contextMenu';
import { DropTargetAnchorContainer } from '../dnd/dropTargetAnchorContainer';
import { themeAbyss } from './theme';
import { ShellManager, } from './dockviewShell';
import { DEFAULT_TAB_GROUP_COLORS, TabGroupColorPalette, } from './tabGroupAccent';
const DEFAULT_ROOT_OVERLAY_MODEL = {
    activationSize: { type: 'pixels', value: 10 },
    size: { type: 'pixels', value: 20 },
};
function buildTabGroupColorPalette(options) {
    var _a;
    const entries = (_a = options.tabGroupColors) !== null && _a !== void 0 ? _a : DEFAULT_TAB_GROUP_COLORS;
    const enabled = options.tabGroupAccent !== 'off';
    return new TabGroupColorPalette(entries, enabled);
}
function moveGroupWithoutDestroying(options) {
    const activePanel = options.from.activePanel;
    const panels = [...options.from.panels].map((panel) => {
        const removedPanel = options.from.model.removePanel(panel);
        options.from.model.renderContainer.detatch(panel);
        return removedPanel;
    });
    panels.forEach((panel) => {
        options.to.model.openPanel(panel, {
            skipSetActive: activePanel !== panel,
            skipSetGroupActive: true,
        });
    });
}
export class DockviewComponent extends BaseGrid {
    get orientation() {
        return this.gridview.orientation;
    }
    get totalPanels() {
        return this.panels.length;
    }
    get panels() {
        return this.groups.flatMap((group) => group.panels);
    }
    get options() {
        return this._options;
    }
    get tabGroupColorPalette() {
        return this._tabGroupColorPalette;
    }
    get activePanel() {
        const activeGroup = this.activeGroup;
        if (!activeGroup) {
            return undefined;
        }
        return activeGroup.activePanel;
    }
    get renderer() {
        var _a;
        return (_a = this.options.defaultRenderer) !== null && _a !== void 0 ? _a : 'onlyWhenVisible';
    }
    get defaultHeaderPosition() {
        var _a;
        return (_a = this.options.defaultHeaderPosition) !== null && _a !== void 0 ? _a : 'top';
    }
    get api() {
        return this._api;
    }
    get floatingGroups() {
        return this._floatingGroups;
    }
    /**
     * Promise that resolves when all popout groups from the last fromJSON call are restored.
     * Useful for tests that need to wait for delayed popout creation.
     */
    get popoutRestorationPromise() {
        return this._popoutRestorationPromise;
    }
    constructor(container, options) {
        var _a, _b, _c, _d, _e, _f, _g;
        super(container, {
            proportionalLayout: true,
            orientation: Orientation.HORIZONTAL,
            styles: options.hideBorders
                ? { separatorBorder: 'transparent' }
                : undefined,
            disableAutoResizing: options.disableAutoResizing,
            locked: options.locked,
            margin: (_b = (_a = options.theme) === null || _a === void 0 ? void 0 : _a.gap) !== null && _b !== void 0 ? _b : 0,
            className: options.className,
        });
        this.nextGroupId = sequentialNumberGenerator();
        this._deserializer = new DefaultDockviewDeserialzier(this);
        this._watermark = null;
        this._popoutPopupServices = new Map();
        this._onWillDragPanel = new Emitter();
        this.onWillDragPanel = this._onWillDragPanel.event;
        this._onWillDragGroup = new Emitter();
        this.onWillDragGroup = this._onWillDragGroup.event;
        this._onDidDrop = new Emitter();
        this.onDidDrop = this._onDidDrop.event;
        this._onWillDrop = new Emitter();
        this.onWillDrop = this._onWillDrop.event;
        this._onWillShowOverlay = new Emitter();
        this.onWillShowOverlay = this._onWillShowOverlay.event;
        this._onUnhandledDragOverEvent = new Emitter();
        this.onUnhandledDragOverEvent = this._onUnhandledDragOverEvent.event;
        this._onDidRemovePanel = new Emitter();
        this.onDidRemovePanel = this._onDidRemovePanel.event;
        this._onDidAddPanel = new Emitter();
        this.onDidAddPanel = this._onDidAddPanel.event;
        this._onDidPopoutGroupSizeChange = new Emitter();
        this.onDidPopoutGroupSizeChange = this._onDidPopoutGroupSizeChange.event;
        this._onDidPopoutGroupPositionChange = new Emitter();
        this.onDidPopoutGroupPositionChange = this._onDidPopoutGroupPositionChange.event;
        this._onDidOpenPopoutWindowFail = new Emitter();
        this.onDidOpenPopoutWindowFail = this._onDidOpenPopoutWindowFail.event;
        this._onDidLayoutFromJSON = new Emitter();
        this.onDidLayoutFromJSON = this._onDidLayoutFromJSON.event;
        this._onDidActivePanelChange = new Emitter({ replay: true });
        this.onDidActivePanelChange = this._onDidActivePanelChange.event;
        this._onDidMovePanel = new Emitter();
        this.onDidMovePanel = this._onDidMovePanel.event;
        this._onDidCreateTabGroup = new Emitter();
        this.onDidCreateTabGroup = this._onDidCreateTabGroup.event;
        this._onDidDestroyTabGroup = new Emitter();
        this.onDidDestroyTabGroup = this._onDidDestroyTabGroup.event;
        this._onDidAddPanelToTabGroup = new Emitter();
        this.onDidAddPanelToTabGroup = this._onDidAddPanelToTabGroup.event;
        this._onDidRemovePanelFromTabGroup = new Emitter();
        this.onDidRemovePanelFromTabGroup = this._onDidRemovePanelFromTabGroup.event;
        this._onDidTabGroupChange = new Emitter();
        this.onDidTabGroupChange = this._onDidTabGroupChange.event;
        this._onDidTabGroupCollapsedChange = new Emitter();
        this.onDidTabGroupCollapsedChange = this._onDidTabGroupCollapsedChange.event;
        this._onDidMaximizedGroupChange = new Emitter();
        this.onDidMaximizedGroupChange = this._onDidMaximizedGroupChange.event;
        this._inShellLayout = false;
        this._edgeGroups = new Map();
        this._edgeGroupDisposables = new Map();
        this._floatingGroups = [];
        this._popoutGroups = [];
        this._popoutRestorationPromise = Promise.resolve();
        this._popoutRestorationCleanups = new Set();
        this._onDidRemoveGroup = new Emitter();
        this.onDidRemoveGroup = this._onDidRemoveGroup.event;
        this._onDidAddGroup = new Emitter();
        this.onDidAddGroup = this._onDidAddGroup.event;
        this._onDidOptionsChange = new Emitter();
        this.onDidOptionsChange = this._onDidOptionsChange.event;
        this._onDidActiveGroupChange = new Emitter();
        this.onDidActiveGroupChange = this._onDidActiveGroupChange.event;
        this._moving = false;
        this._options = options;
        this._tabGroupColorPalette = buildTabGroupColorPalette(options);
        this.popupService = new PopupService(this.element);
        this.contextMenuController = new ContextMenuController(this);
        this._api = new DockviewApi(this);
        // The shell always wraps the dockview element so edge groups can be
        // added at any time via addEdgeGroup() without re-parenting the DOM.
        this.disableResizing = true;
        container.removeChild(this.element);
        this._shellManager = new ShellManager(container, this.element, (w, h) => this._layoutFromShell(w, h), (_d = (_c = options.theme) === null || _c === void 0 ? void 0 : _c.gap) !== null && _d !== void 0 ? _d : 0, (_e = options.theme) === null || _e === void 0 ? void 0 : _e.edgeGroupCollapsedSize);
        // The shell wraps the dockview element, so move the popup anchor
        // into the shell so overflow dropdowns in edge groups position correctly
        this.popupService.updateRoot(this._shellManager.element);
        this._shellThemeClassnames = new Classnames(this._shellManager.element);
        // Anchor the overlay container to the shell element so that edge groups
        // (which live outside this.element in the shell layout) are covered when
        // dndOverlayMounting is 'absolute'.
        this.rootDropTargetContainer = new DropTargetAnchorContainer(this._shellManager.element, { disabled: true });
        this.overlayRenderContainer = new OverlayRenderContainer(this._shellManager.element, this);
        // Hosted in the shell (not inside `.dv-dockview`) so floating overlays
        // share a stacking context with `dv-render-overlay` panels; sized to
        // mirror the gridview rect so saved positions remain valid.
        this._floatingOverlayHost = document.createElement('div');
        this._floatingOverlayHost.className = 'dv-floating-overlay-host';
        this._shellManager.element.appendChild(this._floatingOverlayHost);
        const rootCanDisplayOverlay = (event, position) => {
            const data = getPanelData();
            if (data) {
                if (data.viewId !== this.id) {
                    return false;
                }
                if (position === 'center') {
                    // center drop target is only allowed if there are no panels in the grid
                    // floating panels are allowed
                    return this.gridview.length === 0;
                }
                return true;
            }
            if (position === 'center' && this.gridview.length !== 0) {
                /**
                 * for external events only show the four-corner drag overlays, disable
                 * the center position so that external drag events can fall through to the group
                 * and panel drop target handlers
                 */
                return false;
            }
            const firedEvent = new DockviewUnhandledDragOverEvent(event, 'edge', position, getPanelData);
            this._onUnhandledDragOverEvent.fire(firedEvent);
            return firedEvent.isAccepted;
        };
        this._rootDropTarget = html5Backend.createDropTarget(this.element, {
            className: 'dv-drop-target-edge',
            canDisplayOverlay: rootCanDisplayOverlay,
            acceptedTargetZones: ['top', 'bottom', 'left', 'right', 'center'],
            overlayModel: (_f = options.rootOverlayModel) !== null && _f !== void 0 ? _f : DEFAULT_ROOT_OVERLAY_MODEL,
            getOverrideTarget: () => { var _a; return (_a = this.rootDropTargetContainer) === null || _a === void 0 ? void 0 : _a.model; },
        });
        this._rootPointerDropTarget = pointerBackend.createDropTarget(this.element, {
            className: 'dv-drop-target-edge',
            canDisplayOverlay: rootCanDisplayOverlay,
            acceptedTargetZones: [
                'top',
                'bottom',
                'left',
                'right',
                'center',
            ],
            overlayModel: (_g = options.rootOverlayModel) !== null && _g !== void 0 ? _g : DEFAULT_ROOT_OVERLAY_MODEL,
            getOverrideTarget: () => { var _a; return (_a = this.rootDropTargetContainer) === null || _a === void 0 ? void 0 : _a.model; },
        });
        this.updateDropTargetModel(options);
        toggleClass(this.gridview.element, 'dv-dockview', true);
        toggleClass(this.element, 'dv-debug', !!options.debug);
        this.updateTheme();
        this.updateWatermark();
        if (options.debug) {
            this.addDisposables(new StrictEventsSequencing(this));
        }
        this.addDisposables(this.rootDropTargetContainer, this.overlayRenderContainer, this._onWillDragPanel, this._onWillDragGroup, this._onWillShowOverlay, this._onDidActivePanelChange, this._onDidAddPanel, this._onDidRemovePanel, this._onDidLayoutFromJSON, this._onDidDrop, this._onWillDrop, this._onDidMovePanel, this._onDidMovePanel.event(() => {
            /**
             * Update overlay positions after DOM layout completes to prevent 0×0 dimensions.
             * With defaultRenderer="always" this results in panel content not showing after move operations.
             * Debounced to avoid multiple calls when moving groups with multiple panels.
             */
            this.debouncedUpdateAllPositions();
        }), this._onDidAddGroup, this._onDidRemoveGroup, this._onDidActiveGroupChange, this._onUnhandledDragOverEvent, this._onDidMaximizedGroupChange, this._onDidOptionsChange, this._onDidPopoutGroupSizeChange, this._onDidPopoutGroupPositionChange, this._onDidOpenPopoutWindowFail, this._onDidCreateTabGroup, this._onDidDestroyTabGroup, this._onDidAddPanelToTabGroup, this._onDidRemovePanelFromTabGroup, this._onDidTabGroupChange, this._onDidTabGroupCollapsedChange, this.onDidViewVisibilityChangeMicroTaskQueue(() => {
            this.updateWatermark();
        }), this.onDidAdd((event) => {
            if (!this._moving) {
                this._onDidAddGroup.fire(event);
            }
        }), this.onDidRemove((event) => {
            if (!this._moving) {
                this._onDidRemoveGroup.fire(event);
            }
        }), this.onDidActiveChange((event) => {
            if (!this._moving) {
                this._onDidActiveGroupChange.fire(event);
            }
        }), this.onDidMaximizedChange((event) => {
            this._onDidMaximizedGroupChange.fire({
                group: event.panel,
                isMaximized: event.isMaximized,
            });
        }), Event.any(this.onDidAdd, this.onDidRemove)(() => {
            this.updateWatermark();
        }), Event.any(this.onDidAddPanel, this.onDidRemovePanel, this.onDidAddGroup, this.onDidRemove, this.onDidRemoveGroup, this.onDidMovePanel, this.onDidActivePanelChange, this.onDidPopoutGroupPositionChange, this.onDidPopoutGroupSizeChange, this.onDidCreateTabGroup, this.onDidDestroyTabGroup, this.onDidAddPanelToTabGroup, this.onDidRemovePanelFromTabGroup, this.onDidTabGroupChange, this.onDidTabGroupCollapsedChange)(() => {
            this._bufferOnDidLayoutChange.fire();
        }), Disposable.from(() => {
            var _a;
            // Cancel any pending popout-restoration timers scheduled by
            // fromJSON so they don't open new browser windows after
            // dispose, and resolve their promises so callers awaiting
            // popoutRestorationPromise don't hang. See issue #851.
            for (const cleanup of [...this._popoutRestorationCleanups]) {
                cleanup();
            }
            this._popoutRestorationCleanups.clear();
            // iterate over a copy of the array since .dispose() mutates the original array
            for (const group of [...this._floatingGroups]) {
                group.dispose();
            }
            // iterate over a copy of the array since .dispose() mutates the original array
            for (const group of [...this._popoutGroups]) {
                group.disposable.dispose();
            }
            (_a = this._shellManager) === null || _a === void 0 ? void 0 : _a.dispose();
            for (const d of this._edgeGroupDisposables.values()) {
                d.dispose();
            }
            this._edgeGroupDisposables.clear();
        }), this._rootDropTarget, this._rootPointerDropTarget, Event.any(this._rootDropTarget.onWillShowOverlay, this._rootPointerDropTarget.onWillShowOverlay)((event) => {
            if (this.gridview.length > 0 && event.position === 'center') {
                // option only available when no panels in primary grid
                return;
            }
            this._onWillShowOverlay.fire(new DockviewWillShowOverlayLocationEvent(event, {
                kind: 'edge',
                panel: undefined,
                api: this._api,
                group: undefined,
                getData: getPanelData,
            }));
        }), Event.any(this._rootDropTarget.onDrop, this._rootPointerDropTarget.onDrop)((event) => {
            var _a;
            const willDropEvent = new DockviewWillDropEvent({
                nativeEvent: event.nativeEvent,
                position: event.position,
                panel: undefined,
                api: this._api,
                group: undefined,
                getData: getPanelData,
                kind: 'edge',
            });
            this._onWillDrop.fire(willDropEvent);
            if (willDropEvent.defaultPrevented) {
                return;
            }
            const data = getPanelData();
            if (data) {
                this.moveGroupOrPanel({
                    from: {
                        groupId: data.groupId,
                        panelId: (_a = data.panelId) !== null && _a !== void 0 ? _a : undefined,
                    },
                    to: {
                        group: this.orthogonalize(event.position),
                        position: 'center',
                    },
                });
            }
            else {
                this._onDidDrop.fire(new DockviewDidDropEvent({
                    nativeEvent: event.nativeEvent,
                    position: event.position,
                    panel: undefined,
                    api: this._api,
                    group: undefined,
                    getData: getPanelData,
                }));
            }
        }));
    }
    setVisible(panel, visible) {
        switch (panel.api.location.type) {
            case 'grid':
                super.setVisible(panel, visible);
                break;
            case 'floating': {
                const item = this.floatingGroups.find((floatingGroup) => floatingGroup.group === panel);
                if (item) {
                    item.overlay.setVisible(visible);
                    panel.api._onDidVisibilityChange.fire({
                        isVisible: visible,
                    });
                }
                break;
            }
            case 'popout':
                console.warn('dockview: You cannot hide a group that is in a popout window');
                break;
            case 'edge':
                // Edge group visibility is managed via setEdgeGroupVisible
                break;
        }
    }
    /**
     * Returns the {@link PopupService} that should host popovers (context
     * menus, tab overflow menus) for the given group. Popout groups have their
     * own service rooted in their popout window so the popover renders there
     * and dismisses on events from that window.
     */
    getPopupServiceForGroup(group) {
        var _a;
        return (_a = this._popoutPopupServices.get(group.id)) !== null && _a !== void 0 ? _a : this.popupService;
    }
    addPopoutGroup(itemToPopout, options) {
        var _a, _b, _c, _d, _e, _f;
        if (itemToPopout instanceof DockviewGroupPanel &&
            itemToPopout.model.location.type === 'edge') {
            // edge groups are permanent structural elements and cannot be popped out
            return Promise.resolve(false);
        }
        if (itemToPopout instanceof DockviewPanel &&
            itemToPopout.group.size === 1) {
            return this.addPopoutGroup(itemToPopout.group, options);
        }
        const theme = getDockviewTheme(this.gridview.element);
        const element = this.element;
        function getBox() {
            if (options === null || options === void 0 ? void 0 : options.position) {
                return options.position;
            }
            if (itemToPopout instanceof DockviewGroupPanel) {
                return itemToPopout.element.getBoundingClientRect();
            }
            if (itemToPopout.group) {
                return itemToPopout.group.element.getBoundingClientRect();
            }
            return element.getBoundingClientRect();
        }
        const box = getBox();
        const groupId = (_b = (_a = options === null || options === void 0 ? void 0 : options.overridePopoutGroup) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.getNextGroupId();
        const _window = new PopoutWindow(`${this.id}-${groupId}`, // unique id
        theme !== null && theme !== void 0 ? theme : '', {
            url: (_e = (_c = options === null || options === void 0 ? void 0 : options.popoutUrl) !== null && _c !== void 0 ? _c : (_d = this.options) === null || _d === void 0 ? void 0 : _d.popoutUrl) !== null && _e !== void 0 ? _e : '/popout.html',
            left: window.screenX + box.left,
            top: window.screenY + box.top,
            width: box.width,
            height: box.height,
            onDidOpen: options === null || options === void 0 ? void 0 : options.onDidOpen,
            onWillClose: options === null || options === void 0 ? void 0 : options.onWillClose,
            nonce: (_f = this.options) === null || _f === void 0 ? void 0 : _f.nonce,
        });
        const popoutWindowDisposable = new CompositeDisposable(_window, _window.onDidClose(() => {
            popoutWindowDisposable.dispose();
        }));
        return _window
            .open()
            .then((popoutContainer) => {
            var _a;
            if (_window.isDisposed) {
                return false;
            }
            const referenceGroup = (options === null || options === void 0 ? void 0 : options.referenceGroup)
                ? options.referenceGroup
                : itemToPopout instanceof DockviewPanel
                    ? itemToPopout.group
                    : itemToPopout;
            const referenceLocation = itemToPopout.api.location.type;
            /**
             * The group that is being added doesn't already exist within the DOM, the most likely occurrence
             * of this case is when being called from the `fromJSON(...)` method
             */
            const isGroupAddedToDom = referenceGroup.element.parentElement !== null;
            let group;
            if (!isGroupAddedToDom) {
                group = referenceGroup;
            }
            else if (options === null || options === void 0 ? void 0 : options.overridePopoutGroup) {
                group = options.overridePopoutGroup;
            }
            else {
                group = this.createGroup({ id: groupId });
                if (popoutContainer) {
                    this._onDidAddGroup.fire(group);
                }
            }
            if (popoutContainer === null) {
                console.error('dockview: failed to create popout. perhaps you need to allow pop-ups for this website');
                popoutWindowDisposable.dispose();
                this._onDidOpenPopoutWindowFail.fire();
                // if the popout window was blocked, we need to move the group back to the reference group
                // and set it to visible
                this.movingLock(() => moveGroupWithoutDestroying({
                    from: group,
                    to: referenceGroup,
                }));
                if (!referenceGroup.api.isVisible) {
                    referenceGroup.api.setVisible(true);
                }
                return false;
            }
            const gready = document.createElement('div');
            gready.className = 'dv-overlay-render-container';
            const overlayRenderContainer = new OverlayRenderContainer(gready, this);
            group.model.renderContainer = overlayRenderContainer;
            group.layout(_window.window.innerWidth, _window.window.innerHeight);
            let floatingBox;
            if (!(options === null || options === void 0 ? void 0 : options.overridePopoutGroup) && isGroupAddedToDom) {
                if (itemToPopout instanceof DockviewPanel) {
                    this.movingLock(() => {
                        const panel = referenceGroup.model.removePanel(itemToPopout);
                        group.model.openPanel(panel);
                    });
                }
                else {
                    this.movingLock(() => moveGroupWithoutDestroying({
                        from: referenceGroup,
                        to: group,
                    }));
                    switch (referenceLocation) {
                        case 'grid':
                            referenceGroup.api.setVisible(false);
                            break;
                        case 'floating':
                        case 'popout':
                            floatingBox = (_a = this._floatingGroups
                                .find((value) => value.group.api.id ===
                                itemToPopout.api.id)) === null || _a === void 0 ? void 0 : _a.overlay.toJSON();
                            this.removeGroup(referenceGroup);
                            break;
                    }
                }
            }
            popoutContainer.classList.add('dv-dockview');
            popoutContainer.style.overflow = 'hidden';
            popoutContainer.appendChild(gready);
            popoutContainer.appendChild(group.element);
            const anchor = document.createElement('div');
            const dropTargetContainer = new DropTargetAnchorContainer(anchor, { disabled: this.rootDropTargetContainer.disabled });
            popoutContainer.appendChild(anchor);
            group.model.dropTargetContainer = dropTargetContainer;
            // Each popout group needs its own popover service so that
            // tab context menus, chip menus, and tab overflow menus
            // render in the popout window (not the main window) and
            // their pointerdown/keydown listeners fire on the right
            // window for outside-click and Escape dismissal.
            const popoutPopupService = new PopupService(popoutContainer, _window.window);
            this._popoutPopupServices.set(group.id, popoutPopupService);
            popoutWindowDisposable.addDisposables(popoutPopupService, Disposable.from(() => {
                this._popoutPopupServices.delete(group.id);
            }));
            group.model.location = {
                type: 'popout',
                getWindow: () => _window.window,
                popoutUrl: options === null || options === void 0 ? void 0 : options.popoutUrl,
            };
            if (isGroupAddedToDom &&
                itemToPopout.api.location.type === 'grid') {
                itemToPopout.api.setVisible(false);
            }
            this.doSetGroupAndPanelActive(group);
            popoutWindowDisposable.addDisposables(group.api.onDidActiveChange((event) => {
                var _a;
                if (event.isActive) {
                    (_a = _window.window) === null || _a === void 0 ? void 0 : _a.focus();
                }
            }), group.api.onWillFocus(() => {
                var _a;
                (_a = _window.window) === null || _a === void 0 ? void 0 : _a.focus();
            }));
            let returnedGroup;
            const isValidReferenceGroup = isGroupAddedToDom &&
                referenceGroup &&
                this.getPanel(referenceGroup.id);
            const value = {
                window: _window,
                popoutGroup: group,
                referenceGroup: isValidReferenceGroup
                    ? referenceGroup.id
                    : undefined,
                disposable: {
                    dispose: () => {
                        popoutWindowDisposable.dispose();
                        return returnedGroup;
                    },
                },
            };
            const _onDidWindowPositionChange = onDidWindowMoveEnd(_window.window);
            popoutWindowDisposable.addDisposables(_onDidWindowPositionChange, onDidWindowResizeEnd(_window.window, () => {
                this._onDidPopoutGroupSizeChange.fire({
                    width: _window.window.innerWidth,
                    height: _window.window.innerHeight,
                    group,
                });
            }), _onDidWindowPositionChange.event(() => {
                this._onDidPopoutGroupPositionChange.fire({
                    screenX: _window.window.screenX,
                    screenY: _window.window.screenX,
                    group,
                });
            }), 
            /**
             * ResizeObserver seems slow here, I do not know why but we don't need it
             * since we can reply on the window resize event as we will occupy the full
             * window dimensions
             */
            addDisposableListener(_window.window, 'resize', () => {
                group.layout(_window.window.innerWidth, _window.window.innerHeight);
            }), overlayRenderContainer, Disposable.from(() => {
                if (this.isDisposed) {
                    return; // cleanup may run after instance is disposed
                }
                if (isGroupAddedToDom &&
                    this.getPanel(referenceGroup.id)) {
                    this.movingLock(() => moveGroupWithoutDestroying({
                        from: group,
                        to: referenceGroup,
                    }));
                    if (!referenceGroup.api.isVisible) {
                        referenceGroup.api.setVisible(true);
                    }
                    if (this.getPanel(group.id)) {
                        this.doRemoveGroup(group, {
                            skipPopoutAssociated: true,
                        });
                    }
                }
                else if (this.getPanel(group.id)) {
                    group.model.renderContainer =
                        this.overlayRenderContainer;
                    group.model.dropTargetContainer =
                        this.rootDropTargetContainer;
                    returnedGroup = group;
                    const alreadyRemoved = !this._popoutGroups.find((p) => p.popoutGroup === group);
                    if (alreadyRemoved) {
                        /**
                         * If this popout group was explicitly removed then we shouldn't run the additional
                         * steps. To tell if the running of this disposable is the result of this popout group
                         * being explicitly removed we can check if this popout group is still referenced in
                         * the `this._popoutGroups` list.
                         */
                        return;
                    }
                    if (floatingBox) {
                        this.addFloatingGroup(group, {
                            height: floatingBox.height,
                            width: floatingBox.width,
                            position: floatingBox,
                        });
                    }
                    else {
                        this.doRemoveGroup(group, {
                            skipDispose: true,
                            skipActive: true,
                            skipPopoutReturn: true,
                        });
                        group.model.location = { type: 'grid' };
                        this.movingLock(() => {
                            // suppress group add events since the group already exists
                            this.doAddGroup(group, [0]);
                        });
                    }
                    this.doSetGroupAndPanelActive(group);
                }
            }));
            this._popoutGroups.push(value);
            this.updateWatermark();
            return true;
        })
            .catch((err) => {
            console.error('dockview: failed to create popout.', err);
            return false;
        });
    }
    addFloatingGroup(item, options) {
        var _a, _b, _c, _d, _e, _f;
        if (item instanceof DockviewGroupPanel &&
            item.model.location.type === 'edge') {
            // edge groups are permanent structural elements and cannot be floated
            return;
        }
        let group;
        if (item instanceof DockviewPanel) {
            group = this.createGroup();
            this._onDidAddGroup.fire(group);
            this.movingLock(() => this.removePanel(item, {
                removeEmptyGroup: true,
                skipDispose: true,
                skipSetActiveGroup: true,
            }));
            this.movingLock(() => group.model.openPanel(item, { skipSetGroupActive: true }));
        }
        else {
            group = item;
            const popoutReferenceGroupId = (_a = this._popoutGroups.find((_) => _.popoutGroup === group)) === null || _a === void 0 ? void 0 : _a.referenceGroup;
            const popoutReferenceGroup = popoutReferenceGroupId
                ? this.getPanel(popoutReferenceGroupId)
                : undefined;
            const skip = typeof (options === null || options === void 0 ? void 0 : options.skipRemoveGroup) === 'boolean' &&
                options.skipRemoveGroup;
            if (!skip) {
                if (popoutReferenceGroup) {
                    this.movingLock(() => moveGroupWithoutDestroying({
                        from: item,
                        to: popoutReferenceGroup,
                    }));
                    this.doRemoveGroup(item, {
                        skipPopoutReturn: true,
                        skipPopoutAssociated: true,
                    });
                    this.doRemoveGroup(popoutReferenceGroup, {
                        skipDispose: true,
                    });
                    group = popoutReferenceGroup;
                }
                else {
                    this.doRemoveGroup(item, {
                        skipDispose: true,
                        skipPopoutReturn: true,
                        skipPopoutAssociated: false,
                    });
                }
            }
        }
        function getAnchoredBox() {
            if (options === null || options === void 0 ? void 0 : options.position) {
                const result = {};
                if ('left' in options.position) {
                    result.left = Math.max(options.position.left, 0);
                }
                else if ('right' in options.position) {
                    result.right = Math.max(options.position.right, 0);
                }
                else {
                    result.left = DEFAULT_FLOATING_GROUP_POSITION.left;
                }
                if ('top' in options.position) {
                    result.top = Math.max(options.position.top, 0);
                }
                else if ('bottom' in options.position) {
                    result.bottom = Math.max(options.position.bottom, 0);
                }
                else {
                    result.top = DEFAULT_FLOATING_GROUP_POSITION.top;
                }
                if (typeof options.width === 'number') {
                    result.width = Math.max(options.width, 0);
                }
                else {
                    result.width = DEFAULT_FLOATING_GROUP_POSITION.width;
                }
                if (typeof options.height === 'number') {
                    result.height = Math.max(options.height, 0);
                }
                else {
                    result.height = DEFAULT_FLOATING_GROUP_POSITION.height;
                }
                return result;
            }
            return {
                left: typeof (options === null || options === void 0 ? void 0 : options.x) === 'number'
                    ? Math.max(options.x, 0)
                    : DEFAULT_FLOATING_GROUP_POSITION.left,
                top: typeof (options === null || options === void 0 ? void 0 : options.y) === 'number'
                    ? Math.max(options.y, 0)
                    : DEFAULT_FLOATING_GROUP_POSITION.top,
                width: typeof (options === null || options === void 0 ? void 0 : options.width) === 'number'
                    ? Math.max(options.width, 0)
                    : DEFAULT_FLOATING_GROUP_POSITION.width,
                height: typeof (options === null || options === void 0 ? void 0 : options.height) === 'number'
                    ? Math.max(options.height, 0)
                    : DEFAULT_FLOATING_GROUP_POSITION.height,
            };
        }
        const anchoredBox = getAnchoredBox();
        const overlay = new Overlay(Object.assign(Object.assign({ container: (_b = this._floatingOverlayHost) !== null && _b !== void 0 ? _b : this.gridview.element, content: group.element }, anchoredBox), { minimumInViewportWidth: this.options.floatingGroupBounds === 'boundedWithinViewport'
                ? undefined
                : ((_d = (_c = this.options.floatingGroupBounds) === null || _c === void 0 ? void 0 : _c.minimumWidthWithinViewport) !== null && _d !== void 0 ? _d : DEFAULT_FLOATING_GROUP_OVERFLOW_SIZE), minimumInViewportHeight: this.options.floatingGroupBounds === 'boundedWithinViewport'
                ? undefined
                : ((_f = (_e = this.options.floatingGroupBounds) === null || _e === void 0 ? void 0 : _e.minimumHeightWithinViewport) !== null && _f !== void 0 ? _f : DEFAULT_FLOATING_GROUP_OVERFLOW_SIZE) }));
        const el = group.element.querySelector('.dv-void-container');
        if (!el) {
            throw new Error('dockview: failed to find drag handle');
        }
        overlay.setupDrag(el, {
            inDragMode: typeof (options === null || options === void 0 ? void 0 : options.inDragMode) === 'boolean'
                ? options.inDragMode
                : false,
        });
        const floatingGroupPanel = new DockviewFloatingGroupPanel(group, overlay);
        const disposable = new CompositeDisposable(group.api.onDidActiveChange((event) => {
            if (event.isActive) {
                overlay.bringToFront();
            }
        }), (() => {
            let lastWidth = -1;
            let lastHeight = -1;
            return watchElementResize(group.element, (entry) => {
                const width = Math.round(entry.contentRect.width);
                const height = Math.round(entry.contentRect.height);
                if (width === lastWidth && height === lastHeight) {
                    return;
                }
                lastWidth = width;
                lastHeight = height;
                group.layout(width, height); // let the group know it's size is changing so it can fire events to the panel
            });
        })());
        floatingGroupPanel.addDisposables(overlay.onDidChange(() => {
            // this is either a resize or a move
            // to inform the panels .layout(...) the group with it's current size
            // don't care about resize since the above watcher handles that
            group.layout(group.width, group.height);
        }), overlay.onDidChangeEnd(() => {
            this._bufferOnDidLayoutChange.fire();
        }), group.onDidChange((event) => {
            overlay.setBounds({
                height: event === null || event === void 0 ? void 0 : event.height,
                width: event === null || event === void 0 ? void 0 : event.width,
            });
        }), {
            dispose: () => {
                disposable.dispose();
                remove(this._floatingGroups, floatingGroupPanel);
                group.model.location = { type: 'grid' };
                this.updateWatermark();
            },
        });
        this._floatingGroups.push(floatingGroupPanel);
        group.model.location = { type: 'floating' };
        if (!(options === null || options === void 0 ? void 0 : options.skipActiveGroup)) {
            this.doSetGroupAndPanelActive(group);
        }
        this.updateWatermark();
    }
    orthogonalize(position, options) {
        this.gridview.normalize();
        switch (position) {
            case 'top':
            case 'bottom':
                if (this.gridview.orientation === Orientation.HORIZONTAL) {
                    // we need to add to a vertical splitview but the current root is a horizontal splitview.
                    // insert a vertical splitview at the root level and add the existing view as a child
                    this.gridview.insertOrthogonalSplitviewAtRoot();
                }
                break;
            case 'left':
            case 'right':
                if (this.gridview.orientation === Orientation.VERTICAL) {
                    // we need to add to a horizontal splitview but the current root is a vertical splitview.
                    // insert a horiziontal splitview at the root level and add the existing view as a child
                    this.gridview.insertOrthogonalSplitviewAtRoot();
                }
                break;
            default:
                break;
        }
        switch (position) {
            case 'top':
            case 'left':
            case 'center':
                return this.createGroupAtLocation([0], undefined, options); // insert into first position
            case 'bottom':
            case 'right':
                return this.createGroupAtLocation([this.gridview.length], undefined, options); // insert into last position
            default:
                throw new Error(`dockview: unsupported position ${position}`);
        }
    }
    updateOptions(options) {
        var _a, _b, _c, _d, _e;
        super.updateOptions(options);
        if ('floatingGroupBounds' in options) {
            for (const group of this._floatingGroups) {
                switch (options.floatingGroupBounds) {
                    case 'boundedWithinViewport':
                        group.overlay.minimumInViewportHeight = undefined;
                        group.overlay.minimumInViewportWidth = undefined;
                        break;
                    case undefined:
                        group.overlay.minimumInViewportHeight =
                            DEFAULT_FLOATING_GROUP_OVERFLOW_SIZE;
                        group.overlay.minimumInViewportWidth =
                            DEFAULT_FLOATING_GROUP_OVERFLOW_SIZE;
                        break;
                    default:
                        group.overlay.minimumInViewportHeight =
                            (_a = options.floatingGroupBounds) === null || _a === void 0 ? void 0 : _a.minimumHeightWithinViewport;
                        group.overlay.minimumInViewportWidth =
                            (_b = options.floatingGroupBounds) === null || _b === void 0 ? void 0 : _b.minimumWidthWithinViewport;
                }
                group.overlay.setBounds();
            }
        }
        this.updateDropTargetModel(options);
        const oldDisableDnd = this.options.disableDnd;
        const oldDndStrategy = this.options.dndStrategy;
        this._options = Object.assign(Object.assign({}, this.options), options);
        const newDisableDnd = this.options.disableDnd;
        const newDndStrategy = this.options.dndStrategy;
        if (oldDisableDnd !== newDisableDnd ||
            oldDndStrategy !== newDndStrategy) {
            this.updateDragAndDropState();
        }
        if ('theme' in options) {
            this.updateTheme();
        }
        if ('createRightHeaderActionComponent' in options ||
            'createLeftHeaderActionComponent' in options ||
            'createPrefixHeaderActionComponent' in options) {
            for (const group of this.groups) {
                group.model.updateHeaderActions();
            }
        }
        if ('createWatermarkComponent' in options) {
            if (this._watermark) {
                this._watermark.element.parentElement.remove();
                (_d = (_c = this._watermark).dispose) === null || _d === void 0 ? void 0 : _d.call(_c);
                this._watermark = null;
            }
            this.updateWatermark();
            for (const group of this.groups) {
                group.model.refreshWatermark();
            }
        }
        if ('tabGroupColors' in options || 'tabGroupAccent' in options) {
            this._tabGroupColorPalette.setEntries((_e = this._options.tabGroupColors) !== null && _e !== void 0 ? _e : DEFAULT_TAB_GROUP_COLORS);
            this._tabGroupColorPalette.enabled =
                this._options.tabGroupAccent !== 'off';
            for (const group of this.groups) {
                group.model.refreshTabGroupAccent();
            }
        }
        this._onDidOptionsChange.fire();
        this._layoutFromShell(this.gridview.width, this.gridview.height);
    }
    layout(width, height, forceResize) {
        if (this._shellManager && !this._inShellLayout) {
            this._shellManager.layout(width, height);
        }
        else {
            super.layout(width, height, forceResize);
        }
        this._syncFloatingOverlayHost();
        if (this._floatingGroups) {
            for (const floating of this._floatingGroups) {
                // ensure floting groups stay within visible boundaries
                floating.overlay.setBounds();
            }
        }
    }
    _syncFloatingOverlayHost() {
        if (!this._floatingOverlayHost || !this._shellManager) {
            return;
        }
        const shellRect = this._shellManager.element.getBoundingClientRect();
        const gridRect = this.element.getBoundingClientRect();
        const host = this._floatingOverlayHost;
        host.style.left = `${gridRect.left - shellRect.left}px`;
        host.style.top = `${gridRect.top - shellRect.top}px`;
        host.style.width = `${gridRect.width}px`;
        host.style.height = `${gridRect.height}px`;
    }
    _layoutFromShell(width, height) {
        this._inShellLayout = true;
        this.layout(width, height, true);
        this._inShellLayout = false;
    }
    forceRelayout() {
        if (this._shellManager) {
            this._layoutFromShell(this.width, this.height);
        }
        else {
            super.forceRelayout();
        }
    }
    addEdgeGroup(position, options) {
        if (this._edgeGroups.has(position)) {
            throw new Error(`dockview: edge group already exists at position '${position}'`);
        }
        const group = this.createGroup({ id: options.id });
        group.model.location = { type: 'edge', position };
        group.model.headerPosition = position;
        this._edgeGroups.set(position, group);
        this._onDidAddGroup.fire(group);
        // Collapse when the group becomes empty
        const autoCollapseDisposable = group.model.onDidRemovePanel(() => {
            if (group.model.isEmpty) {
                this.setEdgeGroupCollapsed(group, true);
            }
        });
        this._edgeGroupDisposables.set(position, autoCollapseDisposable);
        this._shellManager.addEdgeView(position, options, group);
        return group.api;
    }
    getEdgeGroup(position) {
        var _a;
        return (_a = this._edgeGroups.get(position)) === null || _a === void 0 ? void 0 : _a.api;
    }
    setEdgeGroupVisible(position, visible) {
        this._shellManager.setEdgeGroupVisible(position, visible);
    }
    isEdgeGroupVisible(position) {
        return this._shellManager.isEdgeGroupVisible(position);
    }
    removeEdgeGroup(position) {
        var _a;
        const group = this._edgeGroups.get(position);
        if (!group) {
            throw new Error(`dockview: no edge group exists at position '${position}'`);
        }
        // Remove panels inside the group first
        for (const panel of [...group.panels]) {
            this.removePanel(panel, {
                removeEmptyGroup: false,
                skipDispose: false,
            });
        }
        // Dispose the auto-collapse listener
        (_a = this._edgeGroupDisposables.get(position)) === null || _a === void 0 ? void 0 : _a.dispose();
        this._edgeGroupDisposables.delete(position);
        // Remove from the shell splitview
        this._shellManager.removeEdgeView(position);
        // Clean up group state
        this._edgeGroups.delete(position);
        group.dispose();
        this._groups.delete(group.id);
        this._onDidRemoveGroup.fire(group);
    }
    setEdgeGroupCollapsed(group, collapsed) {
        for (const [position, edgeGroup] of this._edgeGroups) {
            if (edgeGroup === group) {
                if (this._shellManager.isEdgeGroupCollapsed(position) ===
                    collapsed) {
                    // Skip the splitview resize on a no-op: with non-zero
                    // theme gap, redundant resizeView calls accumulate
                    // rounding drift that gradually shrinks the group.
                    return;
                }
                this._shellManager.setEdgeGroupCollapsed(position, collapsed);
                edgeGroup.api._onDidCollapsedChange.fire({
                    isCollapsed: collapsed,
                });
                return;
            }
        }
    }
    isEdgeGroupCollapsed(group) {
        for (const [position, edgeGroup] of this._edgeGroups) {
            if (edgeGroup === group) {
                return this._shellManager.isEdgeGroupCollapsed(position);
            }
        }
        return false;
    }
    updateDragAndDropState() {
        // Update draggable state for all tabs and void containers
        for (const group of this.groups) {
            group.model.updateDragAndDropState();
        }
    }
    focus() {
        var _a;
        (_a = this.activeGroup) === null || _a === void 0 ? void 0 : _a.focus();
    }
    getGroupPanel(id) {
        return this.panels.find((panel) => panel.id === id);
    }
    setActivePanel(panel) {
        panel.group.model.openPanel(panel);
        this.doSetGroupAndPanelActive(panel.group);
    }
    moveToNext(options = {}) {
        var _a;
        if (!options.group) {
            if (!this.activeGroup) {
                return;
            }
            options.group = this.activeGroup;
        }
        if (options.includePanel && options.group) {
            if (options.group.activePanel !==
                options.group.panels[options.group.panels.length - 1]) {
                options.group.model.moveToNext({ suppressRoll: true });
                return;
            }
        }
        const location = getGridLocation(options.group.element);
        const next = (_a = this.gridview.next(location)) === null || _a === void 0 ? void 0 : _a.view;
        this.doSetGroupAndPanelActive(next);
    }
    moveToPrevious(options = {}) {
        var _a;
        if (!options.group) {
            if (!this.activeGroup) {
                return;
            }
            options.group = this.activeGroup;
        }
        if (options.includePanel && options.group) {
            if (options.group.activePanel !== options.group.panels[0]) {
                options.group.model.moveToPrevious({ suppressRoll: true });
                return;
            }
        }
        const location = getGridLocation(options.group.element);
        const next = (_a = this.gridview.previous(location)) === null || _a === void 0 ? void 0 : _a.view;
        if (next) {
            this.doSetGroupAndPanelActive(next);
        }
    }
    /**
     * Serialize the current state of the layout
     *
     * @returns A JSON respresentation of the layout
     */
    toJSON() {
        var _a;
        const data = this.gridview.serialize();
        const panels = this.panels.reduce((collection, panel) => {
            collection[panel.id] = panel.toJSON();
            return collection;
        }, {});
        const floats = this._floatingGroups.map((group) => {
            return {
                data: group.group.toJSON(),
                position: group.overlay.toJSON(),
            };
        });
        const popoutGroups = this._popoutGroups.map((group) => {
            return {
                data: group.popoutGroup.toJSON(),
                gridReferenceGroup: group.referenceGroup,
                position: group.window.dimensions(),
                url: group.popoutGroup.api.location.type === 'popout'
                    ? group.popoutGroup.api.location.popoutUrl
                    : undefined,
            };
        });
        const result = {
            grid: data,
            panels,
            activeGroup: (_a = this.activeGroup) === null || _a === void 0 ? void 0 : _a.id,
        };
        if (floats.length > 0) {
            result.floatingGroups = floats;
        }
        if (popoutGroups.length > 0) {
            result.popoutGroups = popoutGroups;
        }
        if (this._edgeGroups.size > 0) {
            const shellSerialized = this._shellManager.toJSON();
            // Augment each entry with the serialized group state
            for (const [position, group] of this._edgeGroups) {
                const entry = shellSerialized[position];
                if (entry) {
                    entry.group = group.toJSON();
                }
            }
            result.edgeGroups = shellSerialized;
        }
        return result;
    }
    fromJSON(data, options) {
        var _a, _b, _c;
        // Cancel any popout-restoration timers queued by a previous fromJSON
        // that haven't fired yet. Each cleanup also disposes the orphan group
        // that was registered in _groups synchronously but never parented
        // into a popout window — otherwise the upcoming clear() would call
        // gridview.remove() on an unparented element and throw
        // "Invalid grid element". See issue #1304.
        for (const cleanup of [...this._popoutRestorationCleanups]) {
            cleanup();
        }
        this._popoutRestorationCleanups.clear();
        const existingPanels = new Map();
        let tempGroup;
        if (options === null || options === void 0 ? void 0 : options.reuseExistingPanels) {
            /**
             * What are we doing here?
             *
             * 1. Create a temporary group to hold any panels that currently exist and that also exist in the new layout
             * 2. Remove that temporary group from the group mapping so that it doesn't get cleared when we clear the layout
             */
            tempGroup = this.createGroup();
            this._groups.delete(tempGroup.api.id);
            const newPanels = Object.keys(data.panels);
            for (const panel of this.panels) {
                if (newPanels.includes(panel.api.id)) {
                    existingPanels.set(panel.api.id, panel);
                }
            }
            this.movingLock(() => {
                Array.from(existingPanels.values()).forEach((panel) => {
                    this.moveGroupOrPanel({
                        from: {
                            groupId: panel.api.group.api.id,
                            panelId: panel.api.id,
                        },
                        to: {
                            group: tempGroup,
                            position: 'center',
                        },
                        keepEmptyGroups: true,
                    });
                });
            });
        }
        this.clear();
        if (typeof data !== 'object' || data === null) {
            throw new Error('dockview: serialized layout must be a non-null object');
        }
        const { grid, panels, activeGroup } = data;
        if (grid.root.type !== 'branch' || !Array.isArray(grid.root.data)) {
            throw new Error('dockview: root must be of type branch');
        }
        try {
            // take note of the existing dimensions
            const width = this.width;
            const height = this.height;
            const createGroupFromSerializedState = (data) => {
                const { id, locked, hideHeader, headerPosition, views, activeView, } = data;
                if (typeof id !== 'string') {
                    throw new Error('dockview: group id must be of type string');
                }
                const group = this.createGroup({
                    id,
                    locked: !!locked,
                    hideHeader: !!hideHeader,
                    headerPosition,
                });
                this._onDidAddGroup.fire(group);
                const createdPanels = [];
                for (const child of views) {
                    /**
                     * Run the deserializer step seperately since this may fail to due corrupted external state.
                     * In running this section first we avoid firing lots of 'add' events in the event of a failure
                     * due to a corruption of input data.
                     */
                    const existingPanel = existingPanels.get(child);
                    if (tempGroup && existingPanel) {
                        this.movingLock(() => {
                            tempGroup.model.removePanel(existingPanel);
                        });
                        createdPanels.push(existingPanel);
                        existingPanel.updateFromStateModel(panels[child]);
                    }
                    else {
                        const panel = this._deserializer.fromJSON(panels[child], group);
                        createdPanels.push(panel);
                    }
                }
                for (let i = 0; i < views.length; i++) {
                    const panel = createdPanels[i];
                    const isActive = typeof activeView === 'string' &&
                        activeView === panel.id;
                    const hasExisting = existingPanels.has(panel.api.id);
                    if (hasExisting) {
                        this.movingLock(() => {
                            group.model.openPanel(panel, {
                                skipSetActive: !isActive,
                                skipSetGroupActive: true,
                            });
                        });
                    }
                    else {
                        group.model.openPanel(panel, {
                            skipSetActive: !isActive,
                            skipSetGroupActive: true,
                        });
                    }
                }
                // Restore tab groups before activating a fallback panel so
                // that collapsed groups can clear the active panel correctly.
                if (data.tabGroups && data.tabGroups.length > 0) {
                    group.model.restoreTabGroups(data.tabGroups);
                }
                if (!group.activePanel && group.panels.length > 0) {
                    group.model.openPanel(group.panels[group.panels.length - 1], {
                        skipSetGroupActive: true,
                    });
                }
                return group;
            };
            this.gridview.deserialize(grid, {
                fromJSON: (node) => {
                    return createGroupFromSerializedState(node.data);
                },
            });
            this._layoutFromShell(width, height);
            if (data.edgeGroups) {
                // Auto-create edge groups for positions in the serialized state
                // that don't already have a group registered (e.g. when fromJSON
                // is called before the user has called addEdgeGroup).
                for (const _position of [
                    'top',
                    'bottom',
                    'left',
                    'right',
                ]) {
                    const fixedData = data.edgeGroups[_position];
                    if (fixedData && !this._edgeGroups.has(_position)) {
                        const groupState = fixedData.group;
                        const id = (_a = groupState === null || groupState === void 0 ? void 0 : groupState.id) !== null && _a !== void 0 ? _a : `${_position}-group`;
                        this.addEdgeGroup(_position, { id });
                    }
                }
                // Restore panel contents of edge groups
                for (const [position, edgeGroup] of this._edgeGroups) {
                    const edgeData = data.edgeGroups[position];
                    const groupState = edgeData === null || edgeData === void 0 ? void 0 : edgeData.group;
                    if (groupState) {
                        const { views, activeView } = groupState;
                        const createdPanels = [];
                        for (const panelId of views) {
                            if (panels[panelId]) {
                                const panel = this._deserializer.fromJSON(panels[panelId], edgeGroup);
                                createdPanels.push(panel);
                            }
                        }
                        for (let i = 0; i < createdPanels.length; i++) {
                            const panel = createdPanels[i];
                            const isActive = activeView === panel.id;
                            edgeGroup.model.openPanel(panel, {
                                skipSetActive: !isActive,
                                skipSetGroupActive: true,
                            });
                        }
                        // Restore tab groups before activating a fallback panel
                        if (groupState.tabGroups &&
                            groupState.tabGroups.length > 0) {
                            edgeGroup.model.restoreTabGroups(groupState.tabGroups);
                        }
                        if (!edgeGroup.activePanel &&
                            edgeGroup.panels.length > 0) {
                            edgeGroup.model.openPanel(edgeGroup.panels[edgeGroup.panels.length - 1], { skipSetGroupActive: true });
                        }
                    }
                }
                this._shellManager.fromJSON(data.edgeGroups);
            }
            const serializedFloatingGroups = (_b = data.floatingGroups) !== null && _b !== void 0 ? _b : [];
            for (const serializedFloatingGroup of serializedFloatingGroups) {
                const { data, position } = serializedFloatingGroup;
                const group = createGroupFromSerializedState(data);
                this.addFloatingGroup(group, {
                    position: position,
                    width: position.width,
                    height: position.height,
                    skipRemoveGroup: true,
                    inDragMode: false,
                });
            }
            const serializedPopoutGroups = (_c = data.popoutGroups) !== null && _c !== void 0 ? _c : [];
            // Create a promise that resolves when all popout groups are created
            const popoutPromises = [];
            // Queue popup group creation with delays to avoid browser blocking
            serializedPopoutGroups.forEach((serializedPopoutGroup, index) => {
                const { data, position, gridReferenceGroup, url } = serializedPopoutGroup;
                const group = createGroupFromSerializedState(data);
                // Add a small delay for each popup after the first to avoid browser popup blocking
                const popoutPromise = new Promise((resolve) => {
                    const cleanup = () => {
                        this._popoutRestorationCleanups.delete(cleanup);
                        clearTimeout(handle);
                        // The group was registered in _groups synchronously
                        // but the timer that would parent it into the popout
                        // window never ran. Dispose the orphan here so the
                        // next clear() doesn't trip over an unparented
                        // element. See issue #1304.
                        if (!this.isDisposed &&
                            this._groups.has(group.id) &&
                            group.element.parentElement === null) {
                            for (const panel of [...group.panels]) {
                                this.removePanel(panel, {
                                    removeEmptyGroup: false,
                                });
                            }
                            group.dispose();
                            this._groups.delete(group.id);
                            this._onDidRemoveGroup.fire(group);
                        }
                        resolve();
                    };
                    const handle = setTimeout(() => {
                        this._popoutRestorationCleanups.delete(cleanup);
                        // Guard against the component being disposed before
                        // this timer fires. Under React StrictMode the
                        // component is mounted -> disposed -> remounted, and
                        // without this guard the first instance's queued
                        // restoration would open a second popout window.
                        // See issue #851.
                        if (this.isDisposed) {
                            resolve();
                            return;
                        }
                        this.addPopoutGroup(group, {
                            position: position !== null && position !== void 0 ? position : undefined,
                            overridePopoutGroup: gridReferenceGroup
                                ? group
                                : undefined,
                            referenceGroup: gridReferenceGroup
                                ? this.getPanel(gridReferenceGroup)
                                : undefined,
                            popoutUrl: url,
                        });
                        resolve();
                    }, index * DESERIALIZATION_POPOUT_DELAY_MS); // 100ms delay between each popup
                    this._popoutRestorationCleanups.add(cleanup);
                });
                popoutPromises.push(popoutPromise);
            });
            // Store the promise for tests to wait on
            this._popoutRestorationPromise = Promise.all(popoutPromises).then(() => void 0);
            for (const floatingGroup of this._floatingGroups) {
                floatingGroup.overlay.setBounds();
            }
            if (typeof activeGroup === 'string') {
                const panel = this.getPanel(activeGroup);
                if (panel) {
                    this.doSetGroupAndPanelActive(panel);
                }
            }
        }
        catch (err) {
            console.error('dockview: failed to deserialize layout. Reverting changes', err);
            /**
             * Takes all the successfully created groups and remove all of their panels.
             */
            for (const group of this.groups) {
                for (const panel of group.panels) {
                    this.removePanel(panel, {
                        removeEmptyGroup: false,
                        skipDispose: false,
                    });
                }
            }
            /**
             * To remove a group we cannot call this.removeGroup(...) since this makes assumptions about
             * the underlying HTMLElement existing in the Gridview.
             */
            for (const group of this.groups) {
                group.dispose();
                this._groups.delete(group.id);
                this._onDidRemoveGroup.fire(group);
            }
            // iterate over a reassigned array since original array will be modified
            for (const floatingGroup of [...this._floatingGroups]) {
                floatingGroup.dispose();
            }
            // fires clean-up events and clears the underlying HTML gridview.
            this.clear();
            /**
             * even though we have cleaned-up we still want to inform the caller of their error
             * and we'll do this through re-throwing the original error since afterall you would
             * expect trying to load a corrupted layout to result in an error and not silently fail...
             */
            throw err;
        }
        this.updateWatermark();
        // Force position updates for always visible panels after DOM layout is complete
        this.debouncedUpdateAllPositions();
        this._onDidLayoutFromJSON.fire();
    }
    clear() {
        const groups = Array.from(this._groups.values()).map((_) => _.value);
        const hasActiveGroup = !!this.activeGroup;
        for (const group of groups) {
            if ([...this._edgeGroups.values()].includes(group)) {
                // Edge groups are structural - only clear their panels, not the group itself
                const panels = [...group.panels];
                for (const panel of panels) {
                    this.removePanel(panel, { removeEmptyGroup: false });
                }
                continue;
            }
            // remove the group will automatically remove the panels
            this.removeGroup(group, { skipActive: true });
        }
        if (hasActiveGroup) {
            this.doSetGroupAndPanelActive(undefined);
        }
        this.gridview.clear();
    }
    closeAllGroups() {
        for (const entry of this._groups.entries()) {
            const [_, group] = entry;
            group.value.model.closeAllPanels();
        }
    }
    addPanel(options) {
        var _a, _b;
        if (this.panels.find((_) => _.id === options.id)) {
            throw new Error(`dockview: panel with id ${options.id} already exists`);
        }
        let referenceGroup;
        if (options.position && options.floating) {
            throw new Error('dockview: you can only provide one of: position, floating as arguments to .addPanel(...)');
        }
        const initial = {
            width: options.initialWidth,
            height: options.initialHeight,
        };
        let index;
        if (options.position) {
            if (isPanelOptionsWithPanel(options.position)) {
                const referencePanel = typeof options.position.referencePanel === 'string'
                    ? this.getGroupPanel(options.position.referencePanel)
                    : options.position.referencePanel;
                index = options.position.index;
                if (!referencePanel) {
                    throw new Error(`dockview: referencePanel '${options.position.referencePanel}' does not exist`);
                }
                referenceGroup = this.findGroup(referencePanel);
            }
            else if (isPanelOptionsWithGroup(options.position)) {
                referenceGroup =
                    typeof options.position.referenceGroup === 'string'
                        ? (_a = this._groups.get(options.position.referenceGroup)) === null || _a === void 0 ? void 0 : _a.value
                        : options.position.referenceGroup;
                index = options.position.index;
                if (!referenceGroup) {
                    throw new Error(`dockview: referenceGroup '${options.position.referenceGroup}' does not exist`);
                }
            }
            else {
                const group = this.orthogonalize(directionToPosition(options.position.direction));
                const panel = this.createPanel(options, group);
                group.model.openPanel(panel, {
                    skipSetActive: options.inactive,
                    skipSetGroupActive: options.inactive,
                    index,
                });
                if (!options.inactive) {
                    this.doSetGroupAndPanelActive(group);
                }
                group.api.setSize({
                    height: initial === null || initial === void 0 ? void 0 : initial.height,
                    width: initial === null || initial === void 0 ? void 0 : initial.width,
                });
                return panel;
            }
        }
        else {
            referenceGroup = this.activeGroup;
        }
        let panel;
        if (referenceGroup) {
            const target = toTarget(((_b = options.position) === null || _b === void 0 ? void 0 : _b.direction) || 'within');
            if (options.floating) {
                const group = this.createGroup();
                this._onDidAddGroup.fire(group);
                const floatingGroupOptions = typeof options.floating === 'object' &&
                    options.floating !== null
                    ? options.floating
                    : {};
                this.addFloatingGroup(group, Object.assign(Object.assign({}, floatingGroupOptions), { inDragMode: false, skipRemoveGroup: true, skipActiveGroup: true }));
                panel = this.createPanel(options, group);
                group.model.openPanel(panel, {
                    skipSetActive: options.inactive,
                    skipSetGroupActive: options.inactive,
                    index,
                });
            }
            else if (referenceGroup.api.location.type === 'floating' ||
                referenceGroup.api.location.type === 'edge' ||
                target === 'center') {
                panel = this.createPanel(options, referenceGroup);
                referenceGroup.model.openPanel(panel, {
                    skipSetActive: options.inactive,
                    skipSetGroupActive: options.inactive,
                    index,
                });
                referenceGroup.api.setSize({
                    width: initial === null || initial === void 0 ? void 0 : initial.width,
                    height: initial === null || initial === void 0 ? void 0 : initial.height,
                });
                if (!options.inactive) {
                    this.doSetGroupAndPanelActive(referenceGroup);
                }
            }
            else {
                const location = getGridLocation(referenceGroup.element);
                const relativeLocation = getRelativeLocation(this.gridview.orientation, location, target);
                const group = this.createGroupAtLocation(relativeLocation, this.orientationAtLocation(relativeLocation) ===
                    Orientation.VERTICAL
                    ? initial === null || initial === void 0 ? void 0 : initial.height
                    : initial === null || initial === void 0 ? void 0 : initial.width);
                panel = this.createPanel(options, group);
                group.model.openPanel(panel, {
                    skipSetActive: options.inactive,
                    skipSetGroupActive: options.inactive,
                    index,
                });
                if (!options.inactive) {
                    this.doSetGroupAndPanelActive(group);
                }
            }
        }
        else if (options.floating) {
            const group = this.createGroup();
            this._onDidAddGroup.fire(group);
            const coordinates = typeof options.floating === 'object' &&
                options.floating !== null
                ? options.floating
                : {};
            this.addFloatingGroup(group, Object.assign(Object.assign({}, coordinates), { inDragMode: false, skipRemoveGroup: true, skipActiveGroup: true }));
            panel = this.createPanel(options, group);
            group.model.openPanel(panel, {
                skipSetActive: options.inactive,
                skipSetGroupActive: options.inactive,
                index,
            });
        }
        else {
            const group = this.createGroupAtLocation([0], this.gridview.orientation === Orientation.VERTICAL
                ? initial === null || initial === void 0 ? void 0 : initial.height
                : initial === null || initial === void 0 ? void 0 : initial.width);
            panel = this.createPanel(options, group);
            group.model.openPanel(panel, {
                skipSetActive: options.inactive,
                skipSetGroupActive: options.inactive,
                index,
            });
            if (!options.inactive) {
                this.doSetGroupAndPanelActive(group);
            }
        }
        return panel;
    }
    removePanel(panel, options = {
        removeEmptyGroup: true,
    }) {
        const group = panel.group;
        if (!group) {
            throw new Error(`dockview: cannot remove panel ${panel.id}. it's missing a group.`);
        }
        group.model.removePanel(panel, {
            skipSetActiveGroup: options.skipSetActiveGroup,
        });
        if (!options.skipDispose) {
            panel.group.model.renderContainer.detatch(panel);
            panel.dispose();
        }
        if (group.size === 0 && options.removeEmptyGroup) {
            this.removeGroup(group, { skipActive: options.skipSetActiveGroup });
        }
    }
    createWatermarkComponent() {
        if (this.options.createWatermarkComponent) {
            return this.options.createWatermarkComponent();
        }
        return new Watermark();
    }
    updateWatermark() {
        var _a, _b;
        if (this.groups.filter((x) => x.api.location.type === 'grid' && x.api.isVisible).length === 0) {
            if (!this._watermark) {
                this._watermark = this.createWatermarkComponent();
                this._watermark.init({
                    containerApi: new DockviewApi(this),
                });
                const watermarkContainer = document.createElement('div');
                watermarkContainer.className = 'dv-watermark-container';
                addTestId(watermarkContainer, 'watermark-component');
                watermarkContainer.appendChild(this._watermark.element);
                this.gridview.element.appendChild(watermarkContainer);
            }
        }
        else if (this._watermark) {
            this._watermark.element.parentElement.remove();
            (_b = (_a = this._watermark).dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
            this._watermark = null;
        }
    }
    addGroup(options) {
        var _a;
        if (options) {
            let referenceGroup;
            if (isGroupOptionsWithPanel(options)) {
                const referencePanel = typeof options.referencePanel === 'string'
                    ? this.panels.find((panel) => panel.id === options.referencePanel)
                    : options.referencePanel;
                if (!referencePanel) {
                    throw new Error(`dockview: reference panel ${options.referencePanel} does not exist`);
                }
                referenceGroup = this.findGroup(referencePanel);
                if (!referenceGroup) {
                    throw new Error(`dockview: reference group for reference panel ${options.referencePanel} does not exist`);
                }
            }
            else if (isGroupOptionsWithGroup(options)) {
                referenceGroup =
                    typeof options.referenceGroup === 'string'
                        ? (_a = this._groups.get(options.referenceGroup)) === null || _a === void 0 ? void 0 : _a.value
                        : options.referenceGroup;
                if (!referenceGroup) {
                    throw new Error(`dockview: reference group ${options.referenceGroup} does not exist`);
                }
            }
            else {
                const group = this.orthogonalize(directionToPosition(options.direction), options);
                if (!options.skipSetActive) {
                    this.doSetGroupAndPanelActive(group);
                }
                return group;
            }
            const target = toTarget(options.direction || 'within');
            const location = getGridLocation(referenceGroup.element);
            const relativeLocation = getRelativeLocation(this.gridview.orientation, location, target);
            const group = this.createGroup(options);
            const size = this.getLocationOrientation(relativeLocation) ===
                Orientation.VERTICAL
                ? options.initialHeight
                : options.initialWidth;
            this.doAddGroup(group, relativeLocation, size);
            if (!options.skipSetActive) {
                this.doSetGroupAndPanelActive(group);
            }
            return group;
        }
        else {
            const group = this.createGroup(options);
            this.doAddGroup(group);
            this.doSetGroupAndPanelActive(group);
            return group;
        }
    }
    getLocationOrientation(location) {
        return location.length % 2 == 0 &&
            this.gridview.orientation === Orientation.HORIZONTAL
            ? Orientation.HORIZONTAL
            : Orientation.VERTICAL;
    }
    removeGroup(group, options) {
        this.doRemoveGroup(group, options);
    }
    doRemoveGroup(group, options) {
        var _a;
        // Edge groups are permanent structural elements - never remove them from the layout
        if ([...this._edgeGroups.values()].includes(group)) {
            return group;
        }
        const panels = [...group.panels]; // reassign since group panels will mutate
        if (!(options === null || options === void 0 ? void 0 : options.skipDispose)) {
            for (const panel of panels) {
                this.removePanel(panel, {
                    removeEmptyGroup: false,
                    skipDispose: (_a = options === null || options === void 0 ? void 0 : options.skipDispose) !== null && _a !== void 0 ? _a : false,
                });
            }
        }
        const activePanel = this.activePanel;
        if (group.api.location.type === 'floating') {
            const floatingGroup = this._floatingGroups.find((_) => _.group === group);
            if (floatingGroup) {
                if (!(options === null || options === void 0 ? void 0 : options.skipDispose)) {
                    floatingGroup.group.dispose();
                    this._groups.delete(group.id);
                    this._onDidRemoveGroup.fire(group);
                }
                remove(this._floatingGroups, floatingGroup);
                floatingGroup.dispose();
                if (!(options === null || options === void 0 ? void 0 : options.skipActive) && this._activeGroup === group) {
                    const groups = Array.from(this._groups.values());
                    this.doSetGroupAndPanelActive(groups.length > 0 ? groups[0].value : undefined);
                }
                return floatingGroup.group;
            }
            throw new Error('dockview: failed to find floating group');
        }
        if (group.api.location.type === 'popout') {
            const selectedGroup = this._popoutGroups.find((_) => _.popoutGroup === group);
            if (selectedGroup) {
                if (!(options === null || options === void 0 ? void 0 : options.skipDispose)) {
                    if (!(options === null || options === void 0 ? void 0 : options.skipPopoutAssociated)) {
                        const refGroup = selectedGroup.referenceGroup
                            ? this.getPanel(selectedGroup.referenceGroup)
                            : undefined;
                        if (refGroup && refGroup.panels.length === 0) {
                            this.removeGroup(refGroup);
                        }
                    }
                    selectedGroup.popoutGroup.dispose();
                    this._groups.delete(group.id);
                    this._onDidRemoveGroup.fire(group);
                }
                remove(this._popoutGroups, selectedGroup);
                const removedGroup = selectedGroup.disposable.dispose();
                if (!(options === null || options === void 0 ? void 0 : options.skipPopoutReturn) && removedGroup) {
                    this.doAddGroup(removedGroup, [0]);
                    this.doSetGroupAndPanelActive(removedGroup);
                }
                if (!(options === null || options === void 0 ? void 0 : options.skipActive) && this._activeGroup === group) {
                    const groups = Array.from(this._groups.values());
                    this.doSetGroupAndPanelActive(groups.length > 0 ? groups[0].value : undefined);
                }
                this.updateWatermark();
                return selectedGroup.popoutGroup;
            }
            throw new Error('dockview: failed to find popout group');
        }
        const re = super.doRemoveGroup(group, options);
        if (!(options === null || options === void 0 ? void 0 : options.skipActive)) {
            if (this.activePanel !== activePanel) {
                this._onDidActivePanelChange.fire(this.activePanel);
            }
        }
        return re;
    }
    debouncedUpdateAllPositions() {
        if (this._updatePositionsFrameId !== undefined) {
            cancelAnimationFrame(this._updatePositionsFrameId);
        }
        this._updatePositionsFrameId = requestAnimationFrame(() => {
            this._updatePositionsFrameId = undefined;
            this.overlayRenderContainer.updateAllPositions();
        });
    }
    movingLock(func) {
        const isMoving = this._moving;
        try {
            this._moving = true;
            return func();
        }
        finally {
            this._moving = isMoving;
        }
    }
    moveGroupOrPanel(options) {
        var _a;
        const destinationGroup = options.to.group;
        const sourceGroupId = options.from.groupId;
        const sourceItemId = options.from.panelId;
        const destinationTarget = options.to.position;
        const destinationIndex = options.to.index;
        const sourceGroup = sourceGroupId
            ? (_a = this._groups.get(sourceGroupId)) === null || _a === void 0 ? void 0 : _a.value
            : undefined;
        if (!sourceGroup) {
            throw new Error(`dockview: Failed to find group id ${sourceGroupId}`);
        }
        if (sourceItemId === undefined) {
            if (options.from.tabGroupId) {
                /**
                 * Moving a tab group (subset of panels) into another group
                 */
                this.moveTabGroupToGroup({
                    sourceGroup,
                    tabGroupId: options.from.tabGroupId,
                    destinationGroup,
                    destinationTarget,
                    destinationIndex,
                    skipSetActive: options.skipSetActive,
                    keepEmptyGroups: options.keepEmptyGroups,
                });
            }
            else {
                /**
                 * Moving an entire group into another group
                 */
                this.moveGroup({
                    from: { group: sourceGroup },
                    to: {
                        group: destinationGroup,
                        position: destinationTarget,
                    },
                    skipSetActive: options.skipSetActive,
                });
            }
            return;
        }
        if (!destinationTarget || destinationTarget === 'center') {
            /**
             * Dropping a panel within another group
             */
            const removedPanel = this.movingLock(() => sourceGroup.model.removePanel(sourceItemId, {
                skipSetActive: false,
                skipSetActiveGroup: true,
            }));
            if (!removedPanel) {
                throw new Error(`dockview: No panel with id ${sourceItemId}`);
            }
            if (!options.keepEmptyGroups && sourceGroup.model.size === 0) {
                // remove the group and do not set a new group as active
                this.doRemoveGroup(sourceGroup, { skipActive: true });
            }
            // Check if destination group is empty - if so, force render the component
            const isDestinationGroupEmpty = destinationGroup.model.size === 0;
            this.movingLock(() => {
                var _a;
                return destinationGroup.model.openPanel(removedPanel, {
                    index: destinationIndex,
                    skipSetActive: ((_a = options.skipSetActive) !== null && _a !== void 0 ? _a : false) &&
                        !isDestinationGroupEmpty,
                    skipSetGroupActive: true,
                });
            });
            if (!options.skipSetActive) {
                this.doSetGroupAndPanelActive(destinationGroup);
            }
            this._onDidMovePanel.fire({
                panel: removedPanel,
                from: sourceGroup,
            });
        }
        else {
            /**
             * Dropping a panel to the extremities of a group which will place that panel
             * into an adjacent group
             */
            const referenceLocation = getGridLocation(destinationGroup.element);
            const targetLocation = getRelativeLocation(this.gridview.orientation, referenceLocation, destinationTarget);
            if (sourceGroup.size < 2) {
                /**
                 * If we are moving from a group which only has one panel left we will consider
                 * moving the group itself rather than moving the panel into a newly created group
                 */
                const [targetParentLocation, to] = tail(targetLocation);
                if (sourceGroup.api.location.type === 'grid') {
                    const sourceLocation = getGridLocation(sourceGroup.element);
                    const [sourceParentLocation, from] = tail(sourceLocation);
                    if (sequenceEquals(sourceParentLocation, targetParentLocation)) {
                        // special case when 'swapping' two views within same grid location
                        // if a group has one tab - we are essentially moving the 'group'
                        // which is equivalent to swapping two views in this case
                        this.gridview.moveView(sourceParentLocation, from, to);
                        this._onDidMovePanel.fire({
                            panel: this.getGroupPanel(sourceItemId),
                            from: sourceGroup,
                        });
                        return;
                    }
                }
                if (sourceGroup.api.location.type === 'popout') {
                    /**
                     * the source group is a popout group with a single panel
                     *
                     * 1. remove the panel from the group without triggering any events
                     * 2. remove the popout group — this may cascade-remove the empty
                     *    reference group it left behind in the main grid (see
                     *    doRemoveGroup for popout groups), which can shift grid indices
                     * 3. recompute the target location now that the grid is stable
                     * 4. create a new group at the recomputed location and add that panel
                     */
                    const popoutGroup = this._popoutGroups.find((group) => group.popoutGroup === sourceGroup);
                    const removedPanel = this.movingLock(() => popoutGroup.popoutGroup.model.removePanel(popoutGroup.popoutGroup.panels[0], {
                        skipSetActive: true,
                        skipSetActiveGroup: true,
                    }));
                    this.doRemoveGroup(sourceGroup, { skipActive: true });
                    const updatedTargetLocation = getRelativeLocation(this.gridview.orientation, getGridLocation(destinationGroup.element), destinationTarget);
                    const newGroup = this.createGroupAtLocation(updatedTargetLocation);
                    this.movingLock(() => newGroup.model.openPanel(removedPanel, {
                        skipSetActive: true,
                    }));
                    this.doSetGroupAndPanelActive(newGroup);
                    this._onDidMovePanel.fire({
                        panel: this.getGroupPanel(sourceItemId),
                        from: sourceGroup,
                    });
                    return;
                }
                if (sourceGroup.api.location.type === 'edge') {
                    /**
                     * Edge groups are permanent structural elements — never move the
                     * group itself. Instead extract the panel and create a new grid group,
                     * leaving the edge slot intact (same behaviour as the size >= 2 path).
                     */
                    const removedPanel = this.movingLock(() => sourceGroup.model.removePanel(sourceItemId, {
                        skipSetActive: false,
                        skipSetActiveGroup: true,
                    }));
                    if (!removedPanel) {
                        throw new Error(`dockview: No panel with id ${sourceItemId}`);
                    }
                    const newGroup = this.createGroupAtLocation(targetLocation);
                    this.movingLock(() => newGroup.model.openPanel(removedPanel, {
                        skipSetGroupActive: true,
                    }));
                    this.doSetGroupAndPanelActive(newGroup);
                    this._onDidMovePanel.fire({
                        panel: removedPanel,
                        from: sourceGroup,
                    });
                    return;
                }
                // source group will become empty so delete the group
                const targetGroup = this.movingLock(() => this.doRemoveGroup(sourceGroup, {
                    skipActive: true,
                    skipDispose: true,
                }));
                // after deleting the group we need to re-evaulate the ref location
                const updatedReferenceLocation = getGridLocation(destinationGroup.element);
                const location = getRelativeLocation(this.gridview.orientation, updatedReferenceLocation, destinationTarget);
                this.movingLock(() => this.doAddGroup(targetGroup, location));
                this.doSetGroupAndPanelActive(targetGroup);
                this._onDidMovePanel.fire({
                    panel: this.getGroupPanel(sourceItemId),
                    from: sourceGroup,
                });
            }
            else {
                /**
                 * The group we are removing from has many panels, we need to remove the panels we are moving,
                 * create a new group, add the panels to that new group and add the new group in an appropiate position
                 */
                const removedPanel = this.movingLock(() => sourceGroup.model.removePanel(sourceItemId, {
                    skipSetActive: false,
                    skipSetActiveGroup: true,
                }));
                if (!removedPanel) {
                    throw new Error(`dockview: No panel with id ${sourceItemId}`);
                }
                const dropLocation = getRelativeLocation(this.gridview.orientation, referenceLocation, destinationTarget);
                const group = this.createGroupAtLocation(dropLocation);
                this.movingLock(() => group.model.openPanel(removedPanel, {
                    skipSetGroupActive: true,
                }));
                this.doSetGroupAndPanelActive(group);
                this._onDidMovePanel.fire({
                    panel: removedPanel,
                    from: sourceGroup,
                });
            }
        }
    }
    moveTabGroupToGroup(options) {
        const { sourceGroup, tabGroupId, destinationGroup, destinationTarget, destinationIndex, } = options;
        const tabGroup = sourceGroup.model
            .getTabGroups()
            .find((tg) => tg.id === tabGroupId);
        if (!tabGroup || tabGroup.panelIds.length === 0) {
            return;
        }
        // Snapshot tab group metadata before removing panels
        const label = tabGroup.label;
        const color = tabGroup.color;
        const collapsed = tabGroup.collapsed;
        const componentParams = tabGroup.componentParams;
        const panelIds = [...tabGroup.panelIds];
        // Capture the destination's grid location BEFORE potentially
        // removing the source group, in case source === destination and
        // the source becomes empty after panel removal.
        const referenceLocation = destinationTarget && destinationTarget !== 'center'
            ? getGridLocation(destinationGroup.element)
            : undefined;
        // Remove panels from the source group
        const removedPanels = this.movingLock(() => panelIds
            .map((pid) => sourceGroup.model.removePanel(pid, {
            skipSetActive: false,
            skipSetActiveGroup: true,
        }))
            .filter((p) => p !== undefined));
        if (removedPanels.length === 0) {
            return;
        }
        const addPanelsToGroup = (targetGroup) => {
            this.movingLock(() => {
                for (const panel of removedPanels) {
                    targetGroup.model.openPanel(panel, {
                        index: destinationIndex,
                        skipSetActive: true,
                        skipSetGroupActive: true,
                    });
                }
            });
            // Recreate the tab group in the destination
            const newTabGroup = targetGroup.model.createTabGroup({
                label,
                color,
                collapsed,
                componentParams,
            });
            for (const panel of removedPanels) {
                targetGroup.model.addPanelToTabGroup(newTabGroup.id, panel.id);
            }
            if (!options.skipSetActive) {
                this.doSetGroupAndPanelActive(targetGroup);
            }
            for (const panel of removedPanels) {
                this._onDidMovePanel.fire({
                    panel,
                    from: sourceGroup,
                });
            }
        };
        let targetGroup;
        if (!destinationTarget ||
            destinationTarget === 'center' ||
            !referenceLocation) {
            targetGroup = destinationGroup;
        }
        else {
            const dropLocation = getRelativeLocation(this.gridview.orientation, referenceLocation, destinationTarget);
            targetGroup = this.createGroupAtLocation(dropLocation);
        }
        // Remove the source group if it became empty. We compare against
        // the actual targetGroup (which is a freshly-created group for
        // edge drops) rather than the originally-passed destinationGroup,
        // so a tab-group drag onto its own group's edge still cleans up
        // the now-empty source.
        if (!options.keepEmptyGroups &&
            sourceGroup.model.size === 0 &&
            sourceGroup !== targetGroup) {
            this.doRemoveGroup(sourceGroup, { skipActive: true });
        }
        addPanelsToGroup(targetGroup);
    }
    moveGroup(options) {
        const from = options.from.group;
        const to = options.to.group;
        const target = options.to.position;
        // The group whose panels end up at the target. For non-edge moves
        // we relocate `from` itself; for edge moves we move panels into a
        // freshly created group so the edge slot stays anchored.
        let source = from;
        if (target === 'center') {
            const activePanel = from.activePanel;
            // Snapshot tab group metadata before removing panels so we
            // can recreate the tab groups in the destination after the
            // panels are merged in.
            const tabGroupSnapshots = from.model.getTabGroups().map((tg) => ({
                label: tg.label,
                color: tg.color,
                collapsed: tg.collapsed,
                componentParams: tg.componentParams,
                panelIds: [...tg.panelIds],
            }));
            const panels = this.movingLock(() => [...from.panels].map((p) => from.model.removePanel(p.id, {
                skipSetActive: true,
            })));
            if ((from === null || from === void 0 ? void 0 : from.model.size) === 0) {
                this.doRemoveGroup(from, { skipActive: true });
            }
            this.movingLock(() => {
                for (const panel of panels) {
                    to.model.openPanel(panel, {
                        skipSetActive: panel !== activePanel,
                        skipSetGroupActive: true,
                    });
                }
            });
            for (const snapshot of tabGroupSnapshots) {
                const newTabGroup = to.model.createTabGroup({
                    label: snapshot.label,
                    color: snapshot.color,
                    collapsed: snapshot.collapsed,
                    componentParams: snapshot.componentParams,
                });
                for (const panelId of snapshot.panelIds) {
                    to.model.addPanelToTabGroup(newTabGroup.id, panelId);
                }
            }
            // Ensure group becomes active after move
            if (options.skipSetActive !== true) {
                // For center moves (merges), we need to ensure the target group is active
                // unless explicitly told not to (skipSetActive: true)
                this.doSetGroupAndPanelActive(to);
            }
            else if (!this.activePanel) {
                // Even with skipSetActive: true, ensure there's an active panel if none exists
                // This maintains basic functionality while respecting skipSetActive
                this.doSetGroupAndPanelActive(to);
            }
        }
        else {
            if (from.api.location.type === 'edge') {
                /**
                 * Edge groups are permanent structural elements and must
                 * stay anchored in their edge slot. Move the panels into a
                 * new group; the auto-collapse listener registered in
                 * addEdgeGroup will collapse the now-empty edge slot once
                 * the last panel leaves. The placement code below then
                 * positions `source` like any other moved group.
                 */
                const activePanel = from.activePanel;
                // Snapshot tab group metadata so the new group inherits
                // the tab grouping from the edge slot.
                const tabGroupSnapshots = from.model
                    .getTabGroups()
                    .map((tg) => ({
                    label: tg.label,
                    color: tg.color,
                    collapsed: tg.collapsed,
                    componentParams: tg.componentParams,
                    panelIds: [...tg.panelIds],
                }));
                const movedPanels = this.movingLock(() => [...from.panels].map((p) => from.model.removePanel(p.id, { skipSetActive: true })));
                source = this.createGroup();
                this.movingLock(() => {
                    for (const panel of movedPanels) {
                        source.model.openPanel(panel, {
                            skipSetActive: panel !== activePanel,
                            skipSetGroupActive: true,
                        });
                    }
                });
                for (const snapshot of tabGroupSnapshots) {
                    const newTabGroup = source.model.createTabGroup({
                        label: snapshot.label,
                        color: snapshot.color,
                        collapsed: snapshot.collapsed,
                        componentParams: snapshot.componentParams,
                    });
                    for (const panelId of snapshot.panelIds) {
                        source.model.addPanelToTabGroup(newTabGroup.id, panelId);
                    }
                }
            }
            else {
                switch (from.api.location.type) {
                    case 'grid':
                        this.gridview.removeView(getGridLocation(from.element));
                        break;
                    case 'floating': {
                        const selectedFloatingGroup = this._floatingGroups.find((x) => x.group === from);
                        if (!selectedFloatingGroup) {
                            throw new Error('dockview: failed to find floating group');
                        }
                        selectedFloatingGroup.dispose();
                        break;
                    }
                    case 'popout': {
                        const selectedPopoutGroup = this._popoutGroups.find((x) => x.popoutGroup === from);
                        if (!selectedPopoutGroup) {
                            throw new Error('dockview: failed to find popout group');
                        }
                        // Remove from popout groups list to prevent automatic restoration
                        const index = this._popoutGroups.indexOf(selectedPopoutGroup);
                        if (index >= 0) {
                            this._popoutGroups.splice(index, 1);
                        }
                        // Clean up the reference group (ghost) if it exists and is hidden
                        if (selectedPopoutGroup.referenceGroup) {
                            const referenceGroup = this.getPanel(selectedPopoutGroup.referenceGroup);
                            if (referenceGroup &&
                                !referenceGroup.api.isVisible) {
                                this.doRemoveGroup(referenceGroup, {
                                    skipActive: true,
                                });
                            }
                        }
                        // Manually dispose the window without triggering restoration
                        selectedPopoutGroup.window.dispose();
                        // Update group's location and containers for target
                        if (to.api.location.type === 'grid') {
                            from.model.renderContainer =
                                this.overlayRenderContainer;
                            from.model.dropTargetContainer =
                                this.rootDropTargetContainer;
                            from.model.location = { type: 'grid' };
                        }
                        else if (to.api.location.type === 'floating') {
                            from.model.renderContainer =
                                this.overlayRenderContainer;
                            from.model.dropTargetContainer =
                                this.rootDropTargetContainer;
                            from.model.location = { type: 'floating' };
                        }
                        break;
                    }
                }
            }
            // For moves to grid locations
            if (to.api.location.type === 'grid') {
                const referenceLocation = getGridLocation(to.element);
                const dropLocation = getRelativeLocation(this.gridview.orientation, referenceLocation, target);
                // Add to grid for all moves targeting grid location
                let size;
                switch (this.gridview.orientation) {
                    case Orientation.VERTICAL:
                        size =
                            referenceLocation.length % 2 == 0
                                ? from.api.width
                                : from.api.height;
                        break;
                    case Orientation.HORIZONTAL:
                        size =
                            referenceLocation.length % 2 == 0
                                ? from.api.height
                                : from.api.width;
                        break;
                }
                this.gridview.addView(source, size, dropLocation);
            }
            else if (to.api.location.type === 'floating') {
                // For moves to floating locations, add as floating group
                // Get the position/size from the target floating group
                const targetFloatingGroup = this._floatingGroups.find((x) => x.group === to);
                if (targetFloatingGroup) {
                    const box = targetFloatingGroup.overlay.toJSON();
                    // Calculate position based on available properties
                    let left, top;
                    if ('left' in box) {
                        left = box.left + 50;
                    }
                    else if ('right' in box) {
                        left = Math.max(0, box.right - box.width - 50);
                    }
                    else {
                        left = 50; // Default fallback
                    }
                    if ('top' in box) {
                        top = box.top + 50;
                    }
                    else if ('bottom' in box) {
                        top = Math.max(0, box.bottom - box.height - 50);
                    }
                    else {
                        top = 50; // Default fallback
                    }
                    this.addFloatingGroup(source, {
                        height: box.height,
                        width: box.width,
                        position: {
                            left,
                            top,
                        },
                    });
                }
            }
        }
        source.panels.forEach((panel) => {
            this._onDidMovePanel.fire({ panel, from });
        });
        this.debouncedUpdateAllPositions();
        // Ensure group becomes active after move
        if (options.skipSetActive === false) {
            // Only activate when explicitly requested (skipSetActive: false)
            // Use 'to' group for non-center moves since 'from' may have been destroyed
            const targetGroup = to !== null && to !== void 0 ? to : from;
            this.doSetGroupAndPanelActive(targetGroup);
        }
        else if (source !== from && options.skipSetActive !== true) {
            // Edge group moves create a fresh `source` group; activate it
            // by default so the moved panels receive focus.
            this.doSetGroupAndPanelActive(source);
        }
    }
    doSetGroupActive(group) {
        super.doSetGroupActive(group);
        const activePanel = this.activePanel;
        if (!this._moving &&
            activePanel !== this._onDidActivePanelChange.value) {
            this._onDidActivePanelChange.fire(activePanel);
        }
    }
    doSetGroupAndPanelActive(group) {
        super.doSetGroupActive(group);
        const activePanel = this.activePanel;
        if (group &&
            this.hasMaximizedGroup() &&
            !this.isMaximizedGroup(group)) {
            this.exitMaximizedGroup();
        }
        if (!this._moving &&
            activePanel !== this._onDidActivePanelChange.value) {
            this._onDidActivePanelChange.fire(activePanel);
        }
    }
    getNextGroupId() {
        let id = this.nextGroupId.next();
        while (this._groups.has(id)) {
            id = this.nextGroupId.next();
        }
        return id;
    }
    createGroup(options) {
        if (!options) {
            options = {};
        }
        let id = options === null || options === void 0 ? void 0 : options.id;
        if (id && this._groups.has(options.id)) {
            console.warn(`dockview: Duplicate group id ${options === null || options === void 0 ? void 0 : options.id}. reassigning group id to avoid errors`);
            id = undefined;
        }
        if (!id) {
            id = this.nextGroupId.next();
            while (this._groups.has(id)) {
                id = this.nextGroupId.next();
            }
        }
        const view = new DockviewGroupPanel(this, id, options);
        view.init({ params: {}, accessor: this });
        if (!this._groups.has(view.id)) {
            const disposable = new CompositeDisposable(view.model.onTabDragStart((event) => {
                this._onWillDragPanel.fire(event);
            }), view.model.onGroupDragStart((event) => {
                this._onWillDragGroup.fire(event);
            }), view.model.onMove((event) => {
                const { groupId, itemId, target, index, tabGroupId } = event;
                this.moveGroupOrPanel({
                    from: {
                        groupId: groupId,
                        panelId: itemId,
                        tabGroupId,
                    },
                    to: {
                        group: view,
                        position: target,
                        index,
                    },
                });
            }), view.model.onDidDrop((event) => {
                this._onDidDrop.fire(event);
            }), view.model.onWillDrop((event) => {
                this._onWillDrop.fire(event);
            }), view.model.onWillShowOverlay((event) => {
                if (this.options.disableDnd) {
                    event.preventDefault();
                    return;
                }
                this._onWillShowOverlay.fire(event);
            }), view.model.onUnhandledDragOverEvent((event) => {
                this._onUnhandledDragOverEvent.fire(event);
            }), view.model.onDidAddPanel((event) => {
                if (this._moving) {
                    return;
                }
                this._onDidAddPanel.fire(event.panel);
            }), view.model.onDidRemovePanel((event) => {
                if (this._moving) {
                    return;
                }
                this._onDidRemovePanel.fire(event.panel);
            }), view.model.onDidActivePanelChange((event) => {
                if (this._moving) {
                    return;
                }
                if (event.panel !== this.activePanel) {
                    return;
                }
                if (this._onDidActivePanelChange.value !== event.panel) {
                    this._onDidActivePanelChange.fire(event.panel);
                }
            }), view.model.onDidCreateTabGroup((e) => {
                this._onDidCreateTabGroup.fire(e);
            }), view.model.onDidDestroyTabGroup((e) => {
                this._onDidDestroyTabGroup.fire(e);
            }), view.model.onDidAddPanelToTabGroup((e) => {
                this._onDidAddPanelToTabGroup.fire(e);
            }), view.model.onDidRemovePanelFromTabGroup((e) => {
                this._onDidRemovePanelFromTabGroup.fire(e);
            }), view.model.onDidTabGroupChange((e) => {
                this._onDidTabGroupChange.fire(e);
            }), view.model.onDidTabGroupCollapsedChange((e) => {
                this._onDidTabGroupCollapsedChange.fire(e);
            }), Event.any(view.model.onDidPanelTitleChange, view.model.onDidPanelParametersChange)(() => {
                this._bufferOnDidLayoutChange.fire();
            }));
            this._groups.set(view.id, { value: view, disposable });
        }
        // TODO: must be called after the above listeners have been setup, not an ideal pattern
        view.initialize();
        return view;
    }
    createPanel(options, group) {
        var _a, _b, _c;
        const contentComponent = options.component;
        const tabComponent = (_a = options.tabComponent) !== null && _a !== void 0 ? _a : this.options.defaultTabComponent;
        const view = new DockviewPanelModel(this, options.id, contentComponent, tabComponent);
        const panel = new DockviewPanel(options.id, contentComponent, tabComponent, this, this._api, group, view, {
            renderer: options.renderer,
            minimumWidth: options.minimumWidth,
            minimumHeight: options.minimumHeight,
            maximumWidth: options.maximumWidth,
            maximumHeight: options.maximumHeight,
        });
        panel.init({
            title: (_b = options.title) !== null && _b !== void 0 ? _b : options.id,
            params: (_c = options === null || options === void 0 ? void 0 : options.params) !== null && _c !== void 0 ? _c : {},
        });
        return panel;
    }
    createGroupAtLocation(location, size, options) {
        const group = this.createGroup(options);
        this.doAddGroup(group, location, size);
        return group;
    }
    findGroup(panel) {
        var _a;
        return (_a = Array.from(this._groups.values()).find((group) => group.value.model.containsPanel(panel))) === null || _a === void 0 ? void 0 : _a.value;
    }
    orientationAtLocation(location) {
        const rootOrientation = this.gridview.orientation;
        return location.length % 2 == 1
            ? rootOrientation
            : orthogonal(rootOrientation);
    }
    updateDropTargetModel(options) {
        if ('dndEdges' in options) {
            const disabled = typeof options.dndEdges === 'boolean' &&
                options.dndEdges === false;
            this._rootDropTarget.disabled = disabled;
            this._rootPointerDropTarget.disabled = disabled;
            if (typeof options.dndEdges === 'object' &&
                options.dndEdges !== null) {
                this._rootDropTarget.setOverlayModel(options.dndEdges);
                this._rootPointerDropTarget.setOverlayModel(options.dndEdges);
            }
            else {
                this._rootDropTarget.setOverlayModel(DEFAULT_ROOT_OVERLAY_MODEL);
                this._rootPointerDropTarget.setOverlayModel(DEFAULT_ROOT_OVERLAY_MODEL);
            }
        }
        if ('rootOverlayModel' in options) {
            this.updateDropTargetModel({ dndEdges: options.dndEdges });
        }
    }
    updateTheme() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const theme = (_a = this._options.theme) !== null && _a !== void 0 ? _a : themeAbyss;
        // Apply the theme class only to the shell so edge groups and the
        // main grid both inherit its CSS custom properties via the cascade.
        // Re-declaring it on `.dv-dockview` would block consumer overrides
        // set on the shell from reaching the dockview subtree.
        (_b = this._shellThemeClassnames) === null || _b === void 0 ? void 0 : _b.setClassNames(theme.className);
        this.gridview.margin = (_c = theme.gap) !== null && _c !== void 0 ? _c : 0;
        (_d = this._shellManager) === null || _d === void 0 ? void 0 : _d.updateTheme((_e = theme.gap) !== null && _e !== void 0 ? _e : 0, (_f = theme.edgeGroupCollapsedSize) !== null && _f !== void 0 ? _f : 35);
        if (theme.dndOverlayBorder !== undefined) {
            this.element.style.setProperty('--dv-drag-over-border', theme.dndOverlayBorder);
            (_g = this._shellManager) === null || _g === void 0 ? void 0 : _g.element.style.setProperty('--dv-drag-over-border', theme.dndOverlayBorder);
        }
        else {
            this.element.style.removeProperty('--dv-drag-over-border');
            (_h = this._shellManager) === null || _h === void 0 ? void 0 : _h.element.style.removeProperty('--dv-drag-over-border');
        }
        switch (theme.dndOverlayMounting) {
            case 'absolute':
                this.rootDropTargetContainer.disabled = false;
                break;
            case 'relative':
            default:
                this.rootDropTargetContainer.disabled = true;
                break;
        }
        // Toggle a CSS class so theme stylesheets can scope pure-CSS
        // tab group indicator rules to the 'none' mode only.
        const indicatorNone = ((_j = theme.tabGroupIndicator) !== null && _j !== void 0 ? _j : 'wrap') === 'none';
        toggleClass(this.element, 'dv-tab-group-indicator-none', indicatorNone);
        if (this._shellManager) {
            toggleClass(this._shellManager.element, 'dv-tab-group-indicator-none', indicatorNone);
        }
        // Re-render tab group indicators so the new tabGroupIndicator mode takes effect
        for (const group of this.groups) {
            group.model.updateTabGroups();
        }
    }
}
